import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  LinearProgress,
  Alert,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Dashboard,
  People,
  Hive,
  Sensors,
  Notifications,
  Assessment,
  Science,
  Group,
  TrendingUp,
  Warning,
  CheckCircle,
  Error,
  Info,
  Refresh,
  Edit,
  Delete,
  Visibility,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  AdminPanelSettings
} from '@mui/icons-material';
import { ApiService } from '@api/index';

interface SystemStats {
  users: {
    total: number;
    active: number;
    verified: number;
    newToday: number;
    newWeek: number;
    activeWeek: number;
  };
  hives: {
    total: number;
    active: number;
    statusDistribution: Record<string, number>;
  };
  devices: {
    total: number;
    online: number;
    onlineRate: number;
    statusDistribution: Record<string, number>;
  };
  measurements: {
    total: number;
    week: number;
    dailyAverage: number;
  };
  alerts: {
    totalAlerts: number;
    activeAlerts: number;
    alertsWeek: number;
    totalRules: number;
    activeRules: number;
  };
  inspections: {
    total: number;
    month: number;
    avgCondition: number;
  };
  research: {
    totalProjects: number;
    activeProjects: number;
    recruitingProjects: number;
    totalParticipations: number;
    activeParticipations: number;
  };
}

interface RecentUser {
  id: number;
  name: string;
  email: string;
  isActive: boolean;
  dateJoined: string;
  lastLogin?: string;
  hiveCount?: number;
  deviceCount?: number;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [activeUsers, setActiveUsers] = useState<RecentUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedUser, setSelectedUser] = useState<RecentUser | null>(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load admin dashboard data from backend using ApiService
      const [statsResponse, usersResponse] = await Promise.all([
        ApiService.get('dashboards/stats/'),
        ApiService.get('auth/users/?recent=true&limit=10')
      ]);
      
      setStats(statsResponse.data);
      const usersData = usersResponse.data;
      setRecentUsers(usersData.results || []);
      setActiveUsers(usersData.results?.filter((user: RecentUser) => user.isActive) || []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const getSystemHealthStatus = () => {
    if (!stats) return { status: 'unknown', color: 'default', icon: <Info /> };
    
    const deviceOnlineRate = stats.devices.onlineRate;
    const verificationRate = (stats.users.verified / stats.users.total) * 100;
    
    if (deviceOnlineRate > 80 && verificationRate > 70) {
      return { status: 'healthy', color: 'success', icon: <CheckCircle /> };
    } else if (deviceOnlineRate > 60 && verificationRate > 50) {
      return { status: 'warning', color: 'warning', icon: <Warning /> };
    } else {
      return { status: 'critical', color: 'error', icon: <Error /> };
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    color?: string;
    trend?: { value: number; label: string };
    onClick?: () => void;
  }> = ({ title, value, subtitle, icon, color = '#F8B133', trend, onClick }) => (
    <Card 
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { boxShadow: 3 } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ backgroundColor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {value}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <TrendingUp sx={{ fontSize: 16, mr: 0.5, color: 'success.main' }} />
            <Typography variant="caption" color="success.main">
              +{trend.value} {trend.label}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Loading Admin Dashboard...
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load dashboard data. Please try again.
        </Alert>
      </Box>
    );
  }

  const systemHealth = getSystemHealthStatus();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <AdminPanelSettings sx={{ mr: 2, color: '#F8B133' }} />
            SmartNyuki Admin Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Comprehensive beekeeping platform administration
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{ borderColor: '#F8B133', color: '#F8B133' }}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {/* System Health Alert */}
      <Alert 
        severity={systemHealth.color as any}
        icon={systemHealth.icon}
        sx={{ mb: 3 }}
      >
        System Status: {systemHealth.status.toUpperCase()} - 
        Device Online Rate: {stats.devices.onlineRate}%, 
        User Verification Rate: {((stats.users.verified / stats.users.total) * 100).toFixed(1)}%
      </Alert>

      {/* Main Statistics Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.users.total.toLocaleString()}
            subtitle={`${stats.users.active} active, ${stats.users.verified} verified`}
            icon={<People />}
            trend={{ value: stats.users.newWeek, label: 'this week' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Hives"
            value={stats.hives.total.toLocaleString()}
            subtitle={`${stats.hives.active} active hives`}
            icon={<Hive />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Devices Online"
            value={`${stats.devices.onlineRate}%`}
            subtitle={`${stats.devices.online}/${stats.devices.total} devices`}
            icon={<Sensors />}
            color="#2196f3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Alerts"
            value={stats.alerts.activeAlerts}
            subtitle={`${stats.alerts.alertsWeek} alerts this week`}
            icon={<Notifications />}
            color="#ff9800"
          />
        </Grid>
      </Grid>

      {/* Secondary Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Measurements"
            value={stats.measurements.total.toLocaleString()}
            subtitle={`${stats.measurements.dailyAverage}/day average`}
            icon={<Assessment />}
            color="#9c27b0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Inspections"
            value={stats.inspections.total.toLocaleString()}
            subtitle={`Avg condition: ${stats.inspections.avgCondition}/5`}
            icon={<Visibility />}
            color="#795548"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Research Projects"
            value={stats.research.activeProjects}
            subtitle={`${stats.research.activeParticipations} participants`}
            icon={<Science />}
            color="#607d8b"
          />
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <People sx={{ mr: 1 }} />
              Recent Users
            </Typography>
            <List>
              {recentUsers.map((user) => (
                <ListItem key={user.id}>
                  <ListItemAvatar>
                    <Avatar sx={{ backgroundColor: user.isActive ? '#4caf50' : '#f44336' }}>
                      {user.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.name}
                    secondary={
                      <Box>
                        <Typography variant="body2">{user.email}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Joined: {new Date(user.dateJoined).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        size="small" 
                        label={`${user.hiveCount} hives`}
                        variant="outlined"
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedUser(user);
                          setUserDialogOpen(true);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingUp sx={{ mr: 1 }} />
              System Metrics
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                Device Online Rate
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={stats.devices.onlineRate}
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: stats.devices.onlineRate > 80 ? '#4caf50' : 
                                   stats.devices.onlineRate > 60 ? '#ff9800' : '#f44336'
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {stats.devices.onlineRate}%
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                User Verification Rate
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(stats.users.verified / stats.users.total) * 100}
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#F8B133'
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {((stats.users.verified / stats.users.total) * 100).toFixed(1)}%
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Alert Rules Active
                </Typography>
                <Typography variant="h6">
                  {stats.alerts.activeRules}/{stats.alerts.totalRules}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Research Participation
                </Typography>
                <Typography variant="h6">
                  {stats.research.activeParticipations}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Note: the "Quick Actions" panel (manage users/hives/devices, system settings) was
          removed here — those admin sub-pages (/admin/users, /admin/hives, /admin/devices,
          /admin/settings) don't exist yet. Reintroduce once those pages are built. */}

      {/* User Details Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          User Details: {selectedUser?.name}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={selectedUser.name}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={selectedUser.email}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Status"
                  value={selectedUser.isActive ? 'Active' : 'Inactive'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date Joined"
                  value={new Date(selectedUser.dateJoined).toLocaleDateString()}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hives"
                  value={selectedUser.hiveCount || 0}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Devices"
                  value={selectedUser.deviceCount || 0}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>
            Close
          </Button>
          <Button 
            variant="contained"
            startIcon={<Edit />}
            sx={{ 
              backgroundColor: '#F8B133',
              '&:hover': { backgroundColor: '#f29100' }
            }}
          >
            Edit User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
